// The kiosk's route confinement (UC-PATIENTS-022 step 4; BR-PATIENTS-014): while the device is in kiosk mode every other
// route redirects to /kiosk, and /kiosk itself renders without the shell — no sidebar, no top bar, no search, no shortcuts.
// The session gate is repeated here because the kiosk route sits outside RequireSession on purpose: a locked session shows the
// lock screen inside the kiosk route (the idle timer locks the device), and a session that ended shows sign-in.
//
// How the kiosk is entered (agreed with the patient screens, 22 Sep 2026): Today enters through enterKiosk() before navigating;
// the patient record posts /auth/handover itself and arrives at /kiosk?patient=<id>&form=Consent with the HandoverResponse in
// router state and sessionStorage["vx.handover"], which the kiosk adopts without posting again (the API would answer 409
// already_in_handover); a /kiosk with neither starts its own handover from the query (form, patient) or a new registration.
// Only on arrival: once a kiosk has been shown in this mount, its end (the PIN exit, Next patient, the idle timer) never makes
// the route adopt or start another by itself.
//
// When the session ends (sign-out, "Switch user", a 401 for a revoked or expired session) kiosk mode ends with it: the
// confinement stands down and clears the device, so sign-in is reachable and no patient's pre-fill stays in storage.
//
// No dead end (UC-PATIENTS-022 A7): when the session is in handover but this tab holds no kiosk — the kiosk is open in another
// tab or window, or this tab's storage was cleared — every staff call answers `403 handover_mode` and a new entry is refused.
// The session view's `handover` block, a refusal seen by any query or mutation, or a refused entry then offers staff
// "Exit patient mode": the Control PIN exit, `DELETE /auth/handover` with one Idempotency-Key per attempt.
import { HANDOVER_FORM_TYPES, type HandoverFormType, type SessionView } from "@visionx/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { describeError, isApiError, isNetworkError } from "../api/problem";
import { Kiosk } from "../screens/Kiosk";
import { Lock } from "../screens/Lock";
import { PinSetup } from "../screens/PinSetup";
import { useSession } from "../session/store";
import { useStepUp } from "../stepup/StepUpProvider";
import { Alert, Modal } from "../ui/bits";
import { Icon } from "../ui/icons";
import { adoptHandover, clearKioskDevice, clearStrayHandover, consumeKioskLeft, endKioskLocally, enterKiosk, exitKiosk, handoverIsLive, isHandoverRefusal, leftLocked, noteStrayHandover, pendingHandover, useKiosk, useStrayHandover } from "./mode";

export const KIOSK_PATH = "/kiosk";

/**
 * Mounted beside the routes: in kiosk mode, any other location comes straight back to the kiosk — while a session lasts.
 * Without a kiosk in this tab, a session that is nonetheless handed over is offered the PIN exit (UC-PATIENTS-022 A7).
 */
export function KioskConfinement() {
  const s = useSession();
  const kiosk = useKiosk();
  const loc = useLocation();
  const ended = s.status === "anonymous";
  useHandoverRefusals();
  useEffect(() => { if (ended) clearKioskDevice(); }, [ended]);
  if (kiosk && !ended && loc.pathname !== KIOSK_PATH) return <Navigate to={KIOSK_PATH} replace />;
  if (!kiosk && s.status === "active" && loc.pathname !== KIOSK_PATH) return <StrayHandover session={s.session} />;
  return null;
}

/**
 * The staff routes, drawn only while this device is not in patient mode. The confinement moves the device back to /kiosk in an
 * effect, after a render — so a Back press from the kiosk drew the staff page it went back to, for that moment (review, 5 Oct
 * 2026). Nothing is drawn instead until the device is at /kiosk again.
 */
export function KioskGate({ children }: { children: ReactNode }) {
  const s = useSession();
  const kiosk = useKiosk();
  const loc = useLocation();
  if (kiosk && s.status !== "anonymous" && loc.pathname !== KIOSK_PATH) return null;
  return <>{children}</>;
}

/** Any query or mutation the API refuses with `handover_mode` notes a handover this tab may not hold. */
function useHandoverRefusals(): void {
  const qc = useQueryClient();
  useEffect(() => {
    const seen = (error: unknown) => { if (isHandoverRefusal(error)) noteStrayHandover(); };
    const queries = qc.getQueryCache().subscribe((e) => { if (e.type === "updated" && e.action.type === "error") seen(e.action.error); });
    const mutations = qc.getMutationCache().subscribe((e) => { if (e.type === "updated" && e.action.type === "error") seen(e.action.error); });
    return () => { queries(); mutations(); };
  }, [qc]);
}

/** The exit didn't happen: offline before the prompt could open, or refused or failed after it (UC-PATIENTS-022 E7). */
const leaveProblem = (err: unknown) => (
  <Alert tone="crit"><b>Could not leave patient mode.</b> {isNetworkError(err) && err.offline ? "This device is offline — try again once the connection is back." : describeError(err)}</Alert>
);

/**
 * Leave a handover this tab does not hold: the Control PIN exit (`HandoverExit`), one Idempotency-Key minted per attempt outside
 * the step-up retry. Answers true once the session is out of kiosk mode — also when the API says it already was (`409
 * not_in_handover`, the idle expiry got there first; `exitKiosk` treats that as left) — and false for a cancelled prompt or a
 * failure, which `problem` holds.
 */
function useLeaveHandover(): { leave: () => Promise<boolean>; busy: boolean; problem: unknown } {
  const withStepUp = useStepUp();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<unknown>(null);
  const leave = useCallback(async (): Promise<boolean> => {
    const key = newIdempotencyKey();
    setBusy(true);
    setProblem(null);
    try {
      const r = await withStepUp((g) => exitKiosk(g, key), { title: "Exit patient mode", description: "This session is handed to a patient, so the staff screens cannot load. Leaving patient mode ends it wherever the kiosk is open.", reason: "none" });
      if (r === undefined) return false;
      clearStrayHandover();
      return true;
    } catch (err) {
      setProblem(err);
      return false;
    } finally {
      setBusy(false);
    }
  }, [withStepUp]);
  return { leave, busy, problem };
}

/** "This session is in patient mode": over the staff screens, until staff leave it with the Control PIN or put it away. */
function StrayHandover({ session }: { session: SessionView }) {
  const noted = useStrayHandover();
  const qc = useQueryClient();
  const { leave, busy, problem } = useLeaveHandover();
  const viewed = session.handover && handoverIsLive(session.handover.enteredAt) ? session.handover.enteredAt : null;
  const [putAway, setPutAway] = useState<string | null>(null);
  const open = noted || (viewed !== null && viewed !== putAway);
  // Stable, and quiet while the exit runs: Escape on the PIN prompt above must not also put this notice away.
  const busyRef = useRef(false);
  busyRef.current = busy;
  const close = useCallback(() => {
    if (busyRef.current) return;
    clearStrayHandover();
    setPutAway(viewed);
  }, [viewed]);
  if (!open) return null;
  const exit = async () => {
    if (!(await leave())) return;
    setPutAway(viewed);
    // What the staff screens were refused can be read now.
    void qc.invalidateQueries();
  };
  return (
    <Modal title="This session is in patient mode" onClose={close}
      footer={<>
        <button className="btn" type="button" disabled={busy} onClick={close}>Not now</button>
        <button className="btn primary" type="button" disabled={busy} onClick={() => void exit()}><Icon name="lock" />{busy ? "Leaving…" : "Exit patient mode"}</button>
      </>}>
      <p>The staff screens cannot load while this session is handed to a patient: the kiosk is open in another tab or window, or was left open on this device.</p>
      <p className="muted">Exit patient mode with a Control PIN to carry on here. A kiosk still open elsewhere can then no longer send its form.</p>
      {problem !== null && leaveProblem(problem)}
    </Modal>
  );
}

/** The /kiosk route element: the session gate without the shell, then the kiosk; with no handover it adopts or starts one. */
export function KioskRoute() {
  const s = useSession();
  const kiosk = useKiosk();
  const loc = useLocation();
  const navigate = useNavigate();
  const leaving = useLeaveHandover();
  const [params] = useSearchParams();
  const [outcome, setOutcome] = useState<{ kind: "starting" } | { kind: "left" } | { kind: "failed"; error: unknown } | null>(null);
  const session = s.status === "active" ? s.session : null;
  const patient = params.get("patient");
  const formParam = params.get("form");
  const form: HandoverFormType = (HANDOVER_FORM_TYPES as readonly string[]).includes(formParam ?? "") ? (formParam as HandoverFormType) : "Registration";
  const returnTo = patient ? `/patients/${patient}` : "/today";
  // Whether this mount has shown a kiosk. After it ends here the route starts nothing: the PIN exit navigates on by itself,
  // Next patient enters the next handover itself, and the idle end goes to Today once unlocked. Without this, the exit's own
  // `endKioskLocally()` would look like an arrival without a handover and post a new one (or re-adopt the router state's).
  const hadKiosk = useRef(kiosk !== null);
  // Locked while this device held the kiosk, then unlocked — and the unlock read the session afresh. A session the server no
  // longer holds in handover (it ended the lapsed handover and locked the session before the tablet's own timer did, A2; or
  // staff left patient mode elsewhere) ends patient mode here: the unlock returns to the staff shell, never to a kiosk whose
  // exit the server would refuse before any PIN was asked for (review, 4 Oct 2026). One it still holds resumes.
  const lockedWhileHeld = useRef(false);
  if (kiosk && s.status === "locked") lockedWhileHeld.current = true;
  const released = kiosk !== null && lockedWhileHeld.current && s.status === "active" && !s.session.handover;
  useEffect(() => {
    if (!kiosk) { lockedWhileHeld.current = false; return; }
    if (s.status !== "active" || !lockedWhileHeld.current) return;
    lockedWhileHeld.current = false;
    if (!s.session.handover) leftLocked();
  }, [kiosk, s]);

  useEffect(() => {
    if (kiosk) { hadKiosk.current = true; return; }
    if (!session || outcome !== null) return;
    if (hadKiosk.current) { if (consumeKioskLeft()) setOutcome({ kind: "left" }); return; }
    const handed = pendingHandover(loc.state);
    if (handed) { adoptHandover(handed, session, { returnTo }); return; }
    if (!patient && !formParam && consumeKioskLeft()) { setOutcome({ kind: "left" }); return; }
    setOutcome({ kind: "starting" });
    enterKiosk({ formType: form, patientId: patient ?? undefined, returnTo, session })
      .then(() => setOutcome(null))
      .catch((error: unknown) => setOutcome({ kind: "failed", error }));
  }, [session, kiosk, outcome, loc.state, patient, formParam, form, returnTo]);

  if (s.status === "loading") return null;
  // A session that ended in kiosk mode signs in to Today, not back into patient mode; a bare visit to /kiosk comes back here.
  if (s.status === "anonymous") return <Navigate to="/signin" replace state={kiosk || hadKiosk.current ? undefined : { from: loc.pathname + loc.search }} />;
  if (s.status === "locked") return <Lock />;
  if (s.status === "pin_setup") return <PinSetup />;
  if (kiosk && !released) return <Kiosk key={kiosk.enteredAt} state={kiosk} session={s.session} />;
  if (outcome?.kind === "left") return <Navigate to="/today" replace />;
  if (outcome?.kind === "failed") {
    // Already handed over (UC-PATIENTS-022 E2, A7): the handover this tab does not hold is left here with the PIN, then back.
    const held = isHandoverRefusal(outcome.error);
    const leave = async () => { if (await leaving.leave()) navigate(returnTo, { replace: true }); };
    return (
      <section className="screen" id="s-kiosk" data-bare="true">
        <div className="content">
          <Alert tone="crit"><b>Could not start patient mode.</b> {held ? "This session is already in patient mode: the kiosk is open in another tab or window, or was left open on this device." : describeError(outcome.error)}</Alert>
          {leaving.problem !== null && leaveProblem(leaving.problem)}
          <div className="row c gap-8">
            {held && <button className="btn primary" type="button" disabled={leaving.busy} onClick={() => void leave()}><Icon name="lock" />{leaving.busy ? "Leaving…" : "Exit patient mode"}</button>}
            <Link className="btn" to={returnTo}>Back</Link>
          </div>
        </div>
      </section>
    );
  }
  return <div className="content"><div className="loading" role="status">{hadKiosk.current ? "Leaving patient mode…" : "Starting patient mode…"}</div></div>;
}
